import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { Customer } from '@/types/customer-type';
import { OrderItemResponse, OrderResponse } from '@/types/order-type';
import { calculateOrderTotals } from '@/utils/calc-order';
import { Icon } from '@iconify/react';
import * as React from 'react';
import { useEffect, useMemo } from 'react';

export type AdjustmentMode = 'REFUND' | 'RETURN';

type OrderAdjustmentDialogProps = {
  open: boolean;
  mode: AdjustmentMode;
  onOpenChange: (open: boolean) => void;

  order: OrderResponse;
  orderItems: OrderItemResponse[];
  customer: Customer;

  // called when user confirms refund or return
  onSubmit: (payload: {
    mode: AdjustmentMode;
    amount?: number;
    reason: string;
    restock?: boolean;
  }) => Promise<void> | void;

  submitting?: boolean;
};

export function OrderAdjustmentDialog({
  open,
  mode,
  onOpenChange,
  order,
  onSubmit,
  orderItems,
  customer,
  submitting = false,
}: OrderAdjustmentDialogProps) {
  const isRefund = mode === 'REFUND';

  const [amount, setAmount] = React.useState<string>('');
  const [reason, setReason] = React.useState<string>('');
  const [restock, setRestock] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);
  const { subtotal, shippingFee, discount, tax, total } = useMemo(() => {
    return calculateOrderTotals({
      items: orderItems,
      deliveryFee: Number(order.deliveryFee ?? 0),
      discountPercentage: Number(order.discountPercentage ?? 0),
      taxRate: 0.1, // optional
    });
  }, [orderItems, order.deliveryFee, order.discountPercentage]);
  const maxRefundAmount = total;

  useEffect(() => {
    if (!open) {
      setAmount('');
      setReason('');
      setRestock(true);
      setError(null);
    } else if (isRefund && maxRefundAmount != null) {
      // default to full amount
      setAmount(maxRefundAmount.toFixed(2));
    }
  }, [open, isRefund, maxRefundAmount]);

  const title = isRefund ? 'Refund Order' : 'Return Order';
  const description = isRefund
    ? 'Record a refund for this order. You can refund full or partial amount and keep a reason for audit.'
    : 'Record a product return for this order. Optionally restock the returned items and note the reason.';

  const icon = isRefund ? 'mdi:cash-refund' : 'mdi:package-down';

  const iconClass = isRefund ? 'text-amber-500' : 'text-indigo-500';

  const handleConfirm = async () => {
    setError(null);

    // Basic validation
    if (!reason.trim()) {
      setError('Please provide a reason.');
      return;
    }

    let parsedAmount: number | undefined = undefined;

    if (isRefund) {
      if (!amount.trim()) {
        setError('Please enter a refund amount.');
        return;
      }
      const value = Number(amount);
      if (Number.isNaN(value) || value <= 0) {
        setError('Refund amount must be a positive number.');
        return;
      }
      if (maxRefundAmount != null && value > maxRefundAmount) {
        setError(`Refund amount cannot exceed ${maxRefundAmount.toFixed(2)}.`);
        return;
      }
      parsedAmount = value;
    }

    await onSubmit({
      mode,
      amount: parsedAmount,
      reason: reason.trim(),
      restock: !isRefund ? restock : undefined,
    });

    // if submit doesn't close dialog itself, you can close here:
    // onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="space-y-2">
          <div className="flex items-center gap-2">
            <div
              className={cn(
                'flex h-9 w-9 items-center justify-center rounded-full bg-slate-100',
                isRefund ? 'bg-amber-50' : 'bg-indigo-50',
              )}
            >
              <Icon icon={icon} className={cn('h-5 w-5', iconClass)} />
            </div>
            <div>
              <DialogTitle className="text-base font-semibold text-slate-900">{title}</DialogTitle>
              <DialogDescription className="text-xs text-slate-500">
                {description}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Order summary */}
        {order && (
          <div className="mb-3 rounded-md border border-slate-100 bg-slate-50 px-3 py-2 text-[11px] text-slate-600 space-y-1">
            <div className="flex justify-between">
              <span className="font-medium">Order #{order.orderNumber}</span>
              <span className="font-semibold">
                {order.currency ?? 'USD'}{' '}
                {total.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
            {customer.name && (
              <div className="flex justify-between">
                <span>Customer</span>
                <span className="font-medium">{customer.name}</span>
              </div>
            )}
          </div>
        )}

        <div className="space-y-3">
          {/* Amount (refund only) */}
          {isRefund && (
            <div className="space-y-1.5">
              <label className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                Refund amount
              </label>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="h-9 text-sm"
              />
              {maxRefundAmount != null && (
                <p className="text-[11px] text-slate-400">
                  Max refundable:{' '}
                  <span className="font-medium">
                    {maxRefundAmount.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </p>
              )}
            </div>
          )}

          {/* Reason */}
          <div className="space-y-1.5">
            <label className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
              Reason
            </label>
            <Textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={
                isRefund
                  ? 'Example: Customer changed mind, wrong price, overcharge, etc.'
                  : 'Example: Wrong size delivered, damaged item, customer wants exchange, etc.'
              }
              className="text-sm resize-none"
            />
          </div>

          {/* Restock (return only) */}
          {!isRefund && (
            <div className="flex items-start gap-2 rounded-md bg-slate-50 px-3 py-2">
              <Checkbox
                id="restock-return"
                checked={restock}
                onCheckedChange={(val) => setRestock(val === true)}
                className="mt-0.5"
              />
              <div className="space-y-0.5">
                <label htmlFor="restock-return" className="text-[12px] font-medium text-slate-700">
                  Restock returned items
                </label>
                <p className="text-[11px] text-slate-500">
                  If checked, items will be added back to available stock. If item is damaged,
                  uncheck this and handle as defect stock.
                </p>
              </div>
            </div>
          )}

          {/* Error */}
          {error && <p className="text-[11px] text-red-500 font-medium">{error}</p>}
        </div>

        <DialogFooter className="mt-3 flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleConfirm}
            disabled={submitting}
            className={
              isRefund ? 'bg-amber-500 hover:bg-amber-600' : 'bg-indigo-600 hover:bg-indigo-700'
            }
          >
            {submitting
              ? isRefund
                ? 'Processing refund...'
                : 'Saving return...'
              : isRefund
                ? 'Confirm refund'
                : 'Confirm return'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
